import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  TextField,
} from '@mui/material'
import { CustomButton } from '../../components/restyled-mui/CustomButton'

const useStyles = makeStyles(theme => ({
  textContent: {
    whiteSpace: 'pre-wrap',
    paddingBottom: 24,
  },
  dialogActionPanel: {
    backgroundColor: theme.palette.generalUi.actionPanelBackground,
  },
  closeButton: {
    position: 'absolute',
    top: 2,
    right: 2,
  },
  cancelButton: {
    margin: theme.spacing(0, 2, 0, 0),
    backgroundColor: '#00000000',
    color: theme.palette.text.defaultSub,
    '&:hover': {
      color: theme.palette.text.default,
      backgroundColor: '#00000020',
    },
  },
  defaultButton: {
    margin: theme.spacing(0, 2, 0, 0),
  },
  warningButton: {
    margin: theme.spacing(0, 2, 0, 0),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.mainHover,
    },
  },
}))

export const initialConfirmDialog = {
  open: false,
  confirmCallback: null,
  cancelCallback: null,
  title: '',
  content: '',
  color: '',
}

function normalize(s) {
  return String(s || '').trim()
}

const ConfirmationDialog = props => {
  const classes = useStyles(props)
  const {
    open,
    title,
    content,
    color,
    onClose,
    confirmCallback,
    cancelCallback,
    type,
    expectedText,
    confirmLabel = 'Ok',
    cancelLabel = 'Cancel',
    ...other
  } = props

  const [typed, setTyped] = useState('')

  useEffect(() => {
    if (open) {
      setTyped('')
    }
  }, [open])

  const mustType = Boolean(expectedText)
  const matches = !mustType || normalize(typed) === normalize(expectedText)

  const handleCancel = reason => {
    if (cancelCallback) {
      cancelCallback() // Ensure it resets the dialog state
    } else if (onClose) {
      onClose(reason)
    }
  }

  const handleOk = () => {
    if (!matches) return // guard when typing required
    confirmCallback && confirmCallback()
  }

  return (
    <Dialog
      disableEscapeKeyDown
      maxWidth="sm"
      open={open}
      transitionDuration={0}
      {...other}
    >
      <DialogTitle variant="h3">{title}</DialogTitle>
      <DialogContent className={classes.textContent}>
        {cancelCallback && (
          <IconButton
            aria-label="Close"
            onClick={handleCancel}
            className={classes.closeButton}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}

        {content}

        {mustType && (
          <>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 8, marginBottom: 15 }}
            >
              To confirm, type the setup name exactly: <b>{expectedText}</b>
            </Typography>
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              label="Confirm name"
              placeholder={`Type "${expectedText}" to confirm`}
              value={typed}
              onChange={e => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              error={typed.length > 0 && !matches}
              helperText={
                typed.length > 0 && !matches ? 'Name does not match.' : ' '
              }
              FormHelperTextProps={{ style: { minHeight: 20 } }}
              inputProps={{
                'data-testid': 'confirm-text-input',
                'aria-label': 'confirm setup name',
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActionPanel}>
        {cancelCallback && (
          <CustomButton
            autoFocus
            variant="contained"
            disableElevation
            onClick={handleCancel}
            color="secondary"
            className={classes.cancelButton}
          >
            {cancelLabel}
          </CustomButton>
        )}
        <CustomButton
          variant="contained"
          disableElevation
          onClick={handleOk}
          disabled={!matches}
          color="primary"
          className={
            type === 'warning' ? classes.warningButton : classes.defaultButton
          }
          style={color ? { backgroundColor: color } : undefined}
        >
          {confirmLabel}
        </CustomButton>
      </DialogActions>
    </Dialog>
  )
}

export default ConfirmationDialog
